import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';

const OVERLAY_PATH = '/live-text-banner';

const FONT_SIZE_MIN = 12;
const FONT_SIZE_MAX = 200;
const SPEED_MIN = 10;
const SPEED_MAX = 400;
const MAX_TEXT_LENGTH = 2000;

// The overlay lays the banner out against a phone-width canvas and scales that
// to fit the browser source, so the bar is a fixed strip rather than whatever
// rectangle the source happens to be. Mirrored in public/live-text-banner.html
// — change them in both places or this preview stops matching the stream.
const BANNER_WIDTH = 1080;      // px — full width of TikTok's vertical canvas
const BAR_HEIGHT_RATIO = 1.4;   // bar height as a multiple of the text size

function barHeight(fontSize) {
  return Math.round(fontSize * BAR_HEIGHT_RATIO);
}

const BOLD_WEIGHTS = new Set(['bold', 'bolder', '600', '700', '800', '900']);

function rgbToHex(value) {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    return ('#' + trimmed.slice(1).split('').map(c => c + c).join('')).toLowerCase();
  }
  const m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(trimmed);
  if (!m) return null;
  return '#' + [1, 2, 3].map(i => Number(m[i]).toString(16).padStart(2, '0')).join('').toLowerCase();
}

// --- contenteditable <-> styled runs ----------------------------------------
// The banner is stored as [{ text, bold, italic, underline, color }] rather than
// HTML, so the overlay never has to trust markup. These two functions are the
// bridge between that shape and what the browser's editing commands produce.

// Walks each text node's ancestors up to the editor root. Computed style can't
// be trusted here: text-decoration doesn't inherit, so an underline applied
// above a <b> reads as "none" on the inner element.
function editorToRuns(root) {
  if (!root) return [];
  const runs = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();

  while (node) {
    const text = (node.nodeValue || '').replace(/[\r\n\t]+/g, ' ');
    if (text) {
      let bold = false, italic = false, underline = false, color = null;
      for (let el = node.parentElement; el && el !== root.parentElement; el = el.parentElement) {
        const tag = el.tagName;
        const style = el.style || {};
        if (tag === 'B' || tag === 'STRONG' || BOLD_WEIGHTS.has(String(style.fontWeight))) bold = true;
        if (tag === 'I' || tag === 'EM' || style.fontStyle === 'italic') italic = true;
        if (tag === 'U' || String(style.textDecoration || style.textDecorationLine || '').includes('underline')) underline = true;
        if (!color) {
          const explicit = style.color || (tag === 'FONT' ? el.getAttribute('color') : null);
          if (explicit) color = rgbToHex(explicit);
        }
        if (el === root) break;
      }
      runs.push({ text, bold, italic, underline, color });
    }
    node = walker.nextNode();
  }

  // Merge identically-styled neighbours — typing straight through produces one
  // text node per keystroke batch and they all carry the same styling.
  const merged = [];
  for (const run of runs) {
    const prev = merged[merged.length - 1];
    if (prev && prev.bold === run.bold && prev.italic === run.italic
      && prev.underline === run.underline && prev.color === run.color) {
      prev.text += run.text;
    } else {
      merged.push({ ...run });
    }
  }
  return merged;
}

// Rebuilds editor markup from runs with createElement + textContent only.
function runsToNodes(runs) {
  return runs.map(run => {
    let node = document.createTextNode(run.text);
    if (run.underline) { const u = document.createElement('u'); u.appendChild(node); node = u; }
    if (run.italic) { const i = document.createElement('i'); i.appendChild(node); node = i; }
    if (run.bold) { const b = document.createElement('b'); b.appendChild(node); node = b; }
    const span = document.createElement('span');
    if (run.color) span.style.color = run.color;
    span.appendChild(node);
    return span;
  });
}

function runsText(runs) {
  return runs.map(r => r.text).join('');
}

function sameRuns(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

// --- range field ------------------------------------------------------------
// A slider whose readout doubles as an input: click the number to type an exact
// value. The fill is painted by hand rather than left to the browser's default
// so that the top of the range is a completely full bar.

function RangeField({ label, min, max, unit, value, onChange, hint }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  function startEditing() {
    setDraft(String(value));
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    // A blank or nonsense entry leaves the slider where it was rather than
    // snapping it to a bound the operator never asked for. Blank has to be
    // caught before Number(), which reads '' as 0 — a valid opacity.
    const trimmed = draft.trim();
    if (!trimmed) return;
    const n = Math.round(Number(trimmed));
    if (!Number.isFinite(n)) return;
    onChange(Math.min(max, Math.max(min, n)));
  }

  // The thumb sits centred on the value, so a plain percentage fill lines up
  // with it at both ends: empty at the minimum, edge to edge at the maximum.
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div className="form-group banner-range">
      <label>
        {label} —{' '}
        {editing ? (
          <span className="banner-num-edit">
            <input
              ref={inputRef}
              type="number"
              min={min}
              max={max}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
              }}
              aria-label={`${label} value`}
            />
            {unit}
          </span>
        ) : (
          <button type="button" className="banner-num" onClick={startEditing}
            title="Click to type an exact value">
            {value}{unit}
          </button>
        )}
      </label>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ '--fill': pct + '%' }}
        aria-label={label}
      />
      {hint && <small>{hint}</small>}
    </div>
  );
}

// --- scrolling preview ------------------------------------------------------
// Mirrors public/live-text-banner.html: measure one copy of the message, repeat
// it until it covers the strip twice over, then slide by exactly one copy.

function BannerPreview({ config }) {
  const trackRef = useRef(null);
  const boxRef = useRef(null);
  const shellRef = useRef(null);
  const [width, setWidth] = useState(0);
  const runs = config.content || [];
  const hasText = runs.some(r => r.text && r.text.trim());

  // The preview is the real strip shown at whatever fraction of phone width the
  // card can spare, so its proportions are the proportions on stream — no fixed
  // half scale to go stale the moment the text size changes.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    const measure = () => setWidth(shell.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(shell);
    return () => observer.disconnect();
  }, []);

  const scale = width > 0 ? width / BANNER_WIDTH : 0;

  useEffect(() => {
    const track = trackRef.current;
    const box = boxRef.current;
    if (!track || !box || scale <= 0) return;
    track.getAnimations().forEach(a => a.cancel());
    track.innerHTML = '';
    if (!config.enabled || !hasText) return;

    const gap = Math.max(60, config.font_size * 2.5) * scale;
    const buildChunk = () => {
      const chunk = document.createElement('div');
      chunk.style.flex = 'none';
      chunk.style.paddingRight = gap + 'px';
      for (const run of runs) {
        const span = document.createElement('span');
        span.textContent = run.text;
        if (run.bold) span.style.fontWeight = '800';
        if (run.italic) span.style.fontStyle = 'italic';
        if (run.underline) span.style.textDecoration = 'underline';
        if (run.color) span.style.color = run.color;
        chunk.appendChild(span);
      }
      return chunk;
    };

    // Scroll off mirrors the overlay: one copy, centred, standing still.
    if (config.scroll_enabled === false) {
      const chunk = buildChunk();
      chunk.style.paddingRight = '0';
      track.appendChild(chunk);
      return;
    }

    const probe = buildChunk();
    track.appendChild(probe);
    const chunkWidth = probe.getBoundingClientRect().width;
    if (chunkWidth <= 0) return;

    const copies = Math.max(2, Math.ceil((box.clientWidth * 2) / chunkWidth) + 1);
    for (let i = 1; i < copies; i++) track.appendChild(buildChunk());

    const speed = Math.max(SPEED_MIN, config.scroll_speed) * scale;
    const animation = track.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(${-chunkWidth}px)` }],
      { duration: (chunkWidth / speed) * 1000, iterations: Infinity, easing: 'linear' }
    );
    return () => animation.cancel();
  }, [config, runs, hasText, scale]);

  const bg = config.background_color || '#000000';
  const alpha = config.background_opacity == null ? 1 : config.background_opacity;
  const rgb = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(bg);
  const background = rgb
    ? `rgba(${parseInt(rgb[1], 16)}, ${parseInt(rgb[2], 16)}, ${parseInt(rgb[3], 16)}, ${alpha})`
    : bg;

  return (
    <div className="banner-preview-shell" ref={shellRef}>
      <div
        ref={boxRef}
        className="banner-preview"
        style={{
          background: config.enabled && hasText ? background : 'transparent',
          color: config.text_color,
          fontSize: (config.font_size * scale) + 'px',
          height: (barHeight(config.font_size) * scale) + 'px',
          justifyContent: config.scroll_enabled === false ? 'center' : undefined,
        }}
      >
        <div ref={trackRef} className="banner-preview-track" />
      </div>
      {(!config.enabled || !hasText) && (
        <div className="banner-preview-empty">
          {config.enabled ? 'No message — the overlay stays fully transparent.' : 'Banner is off — the overlay stays fully transparent.'}
        </div>
      )}
    </div>
  );
}

// --- page -------------------------------------------------------------------

export default function LiveBanner() {
  const editorRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [savedConfig, setSavedConfig] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const seededRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [marks, setMarks] = useState({ bold: false, italic: false, underline: false });
  const [presetName, setPresetName] = useState('');
  const [copied, setCopied] = useState(false);
  const [sizeCopied, setSizeCopied] = useState(false);

  const overlayUrl = `${window.location.origin}${OVERLAY_PATH}`;

  const loadEditor = useCallback((runs) => {
    const el = editorRef.current;
    if (!el) return;
    el.innerHTML = '';
    runsToNodes(runs).forEach(node => el.appendChild(node));
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get('/api/live-banner');
        if (cancelled) return;
        setConfig(data.config);
        setSavedConfig(data.config);
        setPresets(data.presets || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load banner settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // The editor is uncontrolled — React must not own its children, or every
  // keystroke would blow away the caret — so it has to be seeded by hand. That
  // cannot happen when the fetch resolves: `loading` is still true then, the
  // component is rendering a spinner, and editorRef is null. Seeding used to
  // ride on requestAnimationFrame and won that race by luck until it didn't,
  // leaving the editor blank on a page that had a live message — one press of
  // Push to Overlay away from wiping it. Wait for the editor to actually exist,
  // and seed exactly once so later edits never yank the caret back.
  useEffect(() => {
    if (loading || !config || seededRef.current || !editorRef.current) return;
    seededRef.current = true;
    loadEditor(config.content || []);
  }, [loading, config, loadEditor]);

  function flash(text) {
    setMessage(text);
    setTimeout(() => setMessage(null), 2500);
  }

  const syncFromEditor = useCallback(() => {
    setConfig(c => (c ? { ...c, content: editorToRuns(editorRef.current) } : c));
  }, []);

  const refreshMarks = useCallback(() => {
    try {
      setMarks({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
      });
    } catch { /* queryCommandState is unsupported outside an editable context */ }
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshMarks);
    return () => document.removeEventListener('selectionchange', refreshMarks);
  }, [refreshMarks]);

  function exec(command, value) {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    // styleWithCSS off keeps bold/italic/underline as <b>/<i>/<u>, which survive
    // a round trip through runs cleanly; colour needs CSS to produce a <span>.
    try { document.execCommand('styleWithCSS', false, command === 'foreColor'); } catch { /* not supported */ }
    document.execCommand(command, false, value);
    syncFromEditor();
    refreshMarks();
  }

  // Drops a bullet in at the caret, spacing it for you: a space on each side,
  // but only where there is not already whitespace there. The overlay never
  // collapses whitespace, so a stray double space would show up on stream.
  function insertBullet() {
    const el = editorRef.current;
    if (!el) return;

    // Read the selection BEFORE focusing. Focusing a contenteditable that has no
    // selection of its own parks the caret at position 0, which would make this
    // check pass and drop the bullet at the very start of the message.
    const prior = window.getSelection();
    const hadCaretInEditor = !!prior && prior.rangeCount > 0 && el.contains(prior.anchorNode);
    el.focus();

    const sel = window.getSelection();
    if (!hadCaretInEditor) {
      const end = document.createRange();
      end.selectNodeContents(el);
      end.collapse(false);
      sel.removeAllRanges();
      sel.addRange(end);
    }
    if (!sel.rangeCount) return;

    // Text on either side of the caret. With a selection active these are the
    // text before its start and after its end — insertText replaces the middle.
    const range = sel.getRangeAt(0);
    const head = range.cloneRange();
    head.collapse(true);
    head.setStart(el, 0);
    const before = head.toString();

    const tail = range.cloneRange();
    tail.collapse(false);
    tail.setEnd(el, el.childNodes.length);
    const after = tail.toString();

    const lead = before.length > 0 && !/\s$/.test(before) ? ' ' : '';
    // No trailing space when the text already continues with one; at the end of
    // the message there is nothing following, so keep it and carry on typing.
    const trail = /^\s/.test(after) ? '' : ' ';
    document.execCommand('insertText', false, `${lead}\u2022${trail}`);
    syncFromEditor();
  }

  function handleKeyDown(e) {
    // One scrolling line — Enter would create a block the overlay can't show.
    if (e.key === 'Enter') { e.preventDefault(); return; }
    if ((e.metaKey || e.ctrlKey) && ['b', 'i', 'u'].includes(e.key.toLowerCase())) {
      e.preventDefault();
      exec({ b: 'bold', i: 'italic', u: 'underline' }[e.key.toLowerCase()]);
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain').replace(/\s+/g, ' ');
    document.execCommand('insertText', false, text);
    syncFromEditor();
  }

  function update(patch) {
    setConfig(c => ({ ...c, ...patch }));
  }

  async function save() {
    if (!config) return;
    setSaving(true);
    setError(null);
    try {
      const runs = editorToRuns(editorRef.current);
      const { data } = await api.put('/api/live-banner', { ...config, content: runs });
      setConfig(data.config);
      setSavedConfig(data.config);
      flash('Banner updated — it is live on the overlay now');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save banner');
    } finally {
      setSaving(false);
    }
  }

  async function savePreset() {
    const name = presetName.trim();
    if (!name) { setError('Give the preset a name first'); return; }
    const runs = editorToRuns(editorRef.current);
    if (!runsText(runs).trim()) { setError('Type a message before saving it as a preset'); return; }
    setError(null);
    try {
      const existing = presets.find(p => p.name.toLowerCase() === name.toLowerCase());
      if (existing) {
        const { data } = await api.put(`/api/live-banner/presets/${existing.id}`, { name, content: runs });
        setPresets(ps => ps.map(p => (p.id === existing.id ? { ...p, ...data.preset } : p)));
        flash(`Preset "${name}" updated`);
      } else {
        const { data } = await api.post('/api/live-banner/presets', { name, content: runs });
        setPresets(ps => [...ps, data.preset].sort((a, b) => a.name.localeCompare(b.name)));
        flash(`Preset "${name}" saved`);
      }
      setPresetName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save preset');
    }
  }

  function loadPreset(preset) {
    loadEditor(preset.content || []);
    update({ content: preset.content || [] });
    setPresetName(preset.name);
    flash(`Loaded "${preset.name}" — press Push to Overlay to put it on stream`);
  }

  async function deletePreset(preset) {
    if (!window.confirm(`Delete the preset "${preset.name}"?`)) return;
    try {
      await api.delete(`/api/live-banner/presets/${preset.id}`);
      setPresets(ps => ps.filter(p => p.id !== preset.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete preset');
    }
  }

  function copyUrl() {
    navigator.clipboard?.writeText(overlayUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => setError('Could not copy — select the URL and copy it manually'));
  }

  // The width is always 1080, so the only number worth putting on the clipboard
  // is the height that goes with the current text size.
  function copySize() {
    if (!config) return;
    navigator.clipboard?.writeText(String(barHeight(config.font_size))).then(() => {
      setSizeCopied(true);
      setTimeout(() => setSizeCopied(false), 2000);
    }).catch(() => setError('Could not copy — type the height into Live Studio by hand'));
  }

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!config) return <div className="card"><p style={{ color: 'var(--danger)' }}>{error || 'Banner settings unavailable'}</p></div>;

  const dirty = !savedConfig
    || !sameRuns(config.content, savedConfig.content)
    || config.font_size !== savedConfig.font_size
    || config.text_color !== savedConfig.text_color
    || config.background_color !== savedConfig.background_color
    || config.background_opacity !== savedConfig.background_opacity
    || config.scroll_speed !== savedConfig.scroll_speed
    || config.scroll_enabled !== savedConfig.scroll_enabled
    || config.enabled !== savedConfig.enabled;

  const charCount = runsText(config.content || []).length;
  const sourceHeight = barHeight(config.font_size);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Live Banner</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {dirty && <span className="badge badge-warning">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Pushing…' : 'Push to Overlay'}
          </button>
        </div>
      </div>

      {error && <div className="card" style={{ borderLeft: '3px solid var(--danger)' }}><p style={{ color: 'var(--danger)', fontSize: '13px', margin: 0 }}>{error}</p></div>}
      {message && <div className="card" style={{ borderLeft: '3px solid var(--success)' }}><p style={{ color: 'var(--success)', fontSize: '13px', margin: 0 }}>{message}</p></div>}

      {/* Message */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Message</h3>
          <label className="banner-switch">
            <input type="checkbox" checked={config.enabled} onChange={e => update({ enabled: e.target.checked })} />
            <span>{config.enabled ? 'Banner on' : 'Banner off'}</span>
          </label>
        </div>

        <div className="banner-toolbar">
          <button type="button" className={`banner-mark ${marks.bold ? 'active' : ''}`}
            onMouseDown={e => e.preventDefault()} onClick={() => exec('bold')} title="Bold (Ctrl/Cmd+B)"><b>B</b></button>
          <button type="button" className={`banner-mark ${marks.italic ? 'active' : ''}`}
            onMouseDown={e => e.preventDefault()} onClick={() => exec('italic')} title="Italic (Ctrl/Cmd+I)"><i>I</i></button>
          <button type="button" className={`banner-mark ${marks.underline ? 'active' : ''}`}
            onMouseDown={e => e.preventDefault()} onClick={() => exec('underline')} title="Underline (Ctrl/Cmd+U)"><u>U</u></button>
          <span className="banner-toolbar-sep" />
          <button type="button" className="banner-mark banner-bullet"
            onMouseDown={e => e.preventDefault()} onClick={insertBullet}
            title="Insert a bullet separator">&bull;</button>
          <span className="banner-toolbar-sep" />
          <label className="banner-color-field" title="Colour the highlighted text">
            Highlight colour
            <input type="color" defaultValue={config.text_color}
              onChange={e => exec('foreColor', e.target.value)} />
          </label>
          <button type="button" className="btn btn-secondary btn-sm"
            onMouseDown={e => e.preventDefault()} onClick={() => exec('removeFormat')}>Clear formatting</button>
          <span className="banner-charcount">{charCount}/{MAX_TEXT_LENGTH}</span>
        </div>

        <div
          ref={editorRef}
          className="banner-editor"
          contentEditable
          suppressContentEditableWarning
          spellCheck
          role="textbox"
          aria-label="Banner message"
          onInput={syncFromEditor}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onKeyUp={refreshMarks}
          onMouseUp={refreshMarks}
        />
        <p className="banner-hint">
          Select any part of the message to bold, italicise, underline or recolour just that part.
          Everything sits on one scrolling line — line breaks are flattened to spaces.
        </p>
      </div>

      {/* Preview */}
      <div className="card">
        <h3 className="card-title" style={{ marginBottom: '12px' }}>Preview</h3>
        <BannerPreview config={config} />
        <p className="banner-hint" style={{ marginTop: '10px' }}>
          Shown at the real proportions — the bar is a fixed {BANNER_WIDTH} &times; {barHeight(config.font_size)} strip,
          the full width of the phone screen and just tall enough to clear the text. It stays that
          shape however long the message is; only the text size changes it.
        </p>
      </div>

      {/* Appearance */}
      <div className="card">
        <h3 className="card-title" style={{ marginBottom: '16px' }}>Appearance</h3>
        <div className="banner-controls">
          <RangeField label="Text size" unit="px" min={FONT_SIZE_MIN} max={FONT_SIZE_MAX}
            value={config.font_size} onChange={v => update({ font_size: v })} />
          <div className="form-group">
            <label className="banner-switch">
              <input type="checkbox" checked={config.scroll_enabled !== false}
                onChange={e => update({ scroll_enabled: e.target.checked })} />
              <span>{config.scroll_enabled !== false ? 'Scrolling on' : 'Scrolling off'}</span>
            </label>
            <small>
              {config.scroll_enabled !== false
                ? 'The message loops across the bar.'
                : 'The message sits still, centred in the bar. Anything wider than the bar is cut off at the edges.'}
            </small>
          </div>
          {config.scroll_enabled !== false && (
            <RangeField label="Scroll speed" unit="px/sec" min={SPEED_MIN} max={SPEED_MAX}
              value={config.scroll_speed} onChange={v => update({ scroll_speed: v })} />
          )}
          <div className="form-group">
            <label>Text colour</label>
            <div className="banner-color-row">
              <input type="color" value={config.text_color} onChange={e => update({ text_color: e.target.value })} />
              <span>{config.text_color}</span>
            </div>
            <small>Applies to any text you haven't given its own colour.</small>
          </div>
          <div className="form-group">
            <label>Background colour</label>
            <div className="banner-color-row">
              <input type="color" value={config.background_color} onChange={e => update({ background_color: e.target.value })} />
              <span>{config.background_color}</span>
            </div>
          </div>
          <RangeField label="Background opacity" unit="%" min={0} max={100}
            value={Math.round(config.background_opacity * 100)}
            onChange={v => update({ background_opacity: v / 100 })}
            hint="Drop to 0% for text floating straight over the camera." />
        </div>
      </div>

      {/* Presets */}
      <div className="card">
        <h3 className="card-title" style={{ marginBottom: '12px' }}>Saved messages</h3>
        <div className="banner-preset-save">
          <input type="text" placeholder="Preset name (e.g. Shipping Mon &amp; Thurs)" value={presetName}
            onChange={e => setPresetName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); savePreset(); } }} />
          <button className="btn btn-secondary" onClick={savePreset}>Save current message</button>
        </div>
        {presets.length === 0 ? (
          <p className="banner-hint" style={{ marginTop: '12px' }}>
            No saved messages yet. Type one above, name it, and it will be one click away next stream.
          </p>
        ) : (
          <div className="banner-preset-list">
            {presets.map(preset => (
              <div key={preset.id} className="banner-preset">
                <div className="banner-preset-main">
                  <strong>{preset.name}</strong>
                  <span>{runsText(preset.content || [])}</span>
                </div>
                <div className="banner-preset-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => loadPreset(preset)}>Load</button>
                  <button className="btn btn-danger btn-sm" onClick={() => deletePreset(preset)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* OBS setup */}
      <div className="card">
        <h3 className="card-title" style={{ marginBottom: '12px' }}>Add it to TikTok Live Studio</h3>
        <div className="banner-url-row">
          <code>{overlayUrl}</code>
          <button className="btn btn-secondary btn-sm" onClick={copyUrl}>{copied ? 'Copied' : 'Copy URL'}</button>
        </div>
        <div className="banner-size-row">
          <span>Browser source size</span>
          <code>{BANNER_WIDTH} &times; {sourceHeight}</code>
          <button className="btn btn-secondary btn-sm" onClick={copySize}>
            {sizeCopied ? 'Copied' : `Copy ${sourceHeight}`}
          </button>
        </div>
        <ol className="banner-steps">
          <li>Add a <strong>Browser</strong> source to the scene and paste the URL above.</li>
          <li>
            Set its width to <strong>{BANNER_WIDTH}</strong> and its height to <strong>{sourceHeight}</strong> —
            full phone width, and just enough height to sit a little above and below the text.
          </li>
          <li>Drag it into place under the face camera, resizing from a corner so it keeps its shape.</li>
          <li>Leave "Shutdown source when not visible" off so the banner keeps its place between scenes.</li>
          <li>Anything you push from this page appears on the overlay within a second. No need to refresh the source.</li>
        </ol>
        <p className="banner-hint">
          A square or oversized source is no longer a square black box — the bar draws itself at these
          proportions inside whatever rectangle it is given, centred, with the rest fully transparent.
          Matching the source to the size above just stops you having to line the strip up by eye.
          Change the text size and come back here for the new height.
        </p>
      </div>
    </div>
  );
}
